// AutofillPanel — "Autofill from notes" for a Feature launch or a Release.
//
// Paste a blurb → the LLM proposes field values → we show a per-field diff of
// current → proposed → the editor commits the checked subset. Nothing is
// written until Apply: the preview endpoints are read-only, and the commit
// runs through the ordinary PATCH / set-items mutations. Proposed changes are
// PRE-CHECKED (including clobbers) — new info is usually the better copy — so
// the editor unchecks what they want to keep rather than opting in per field.
import { useEffect, useMemo, useState } from "react";
import type { EnrichedFeature, Release, ReleaseItem } from "@release-hub/shared";
import {
  useAutofillFeaturePreview,
  useAutofillReleasePreview,
  usePatchFeature,
  usePatchRelease,
  useSetReleaseItems,
  type AutofillReleaseItem,
  type FeatureAutofillFields,
} from "@/api/mutations";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/ui/dialog";
import { Button } from "@/ui/button";
import { Loading } from "@/ui/spinner";

type Props =
  | {
      kind: "feature";
      entityId: string;
      current: EnrichedFeature;
      open: boolean;
      onOpenChange: (v: boolean) => void;
      /** Text to pre-fill the blurb with each time the panel opens. Empty
       *  for features; the GitHub body for releases (erasable). */
      defaultBlurb?: string;
    }
  | {
      kind: "release";
      entityId: string;
      current: Release & { items?: ReleaseItem[] };
      open: boolean;
      onOpenChange: (v: boolean) => void;
      defaultBlurb?: string;
    };

/** Human labels for the feature fields the extractor can fill. */
const FEATURE_LABELS: Record<keyof FeatureAutofillFields, string> = {
  name: "Name",
  pitch: "Pitch",
  feature_family: "Family",
  audience: "Audience",
  announcement_narrative: "Announcement narrative",
  known_gaps: "Known gaps",
  web_note: "Web note",
  mobile_note: "Mobile note",
  beta: "Beta",
  surface: "Surface",
  announcement_state: "Access",
  ea_date: "EA date",
  ga_date: "GA target",
};
// Stable order for the diff list.
const FEATURE_ORDER = Object.keys(FEATURE_LABELS) as (keyof FeatureAutofillFields)[];

function show(v: unknown): string {
  if (v == null || v === "") return "";
  if (Array.isArray(v)) return v.map((x) => String(x)).join(" · ");
  if (typeof v === "boolean") return v ? "yes" : "no";
  return String(v);
}

export function AutofillPanel(props: Props) {
  const { kind, entityId, open, onOpenChange, defaultBlurb } = props;
  const [blurb, setBlurb] = useState("");
  // Release-only: persist the pasted notes as the release's source_body_override
  // so future generates start from them. Folds the old standalone "LLM source
  // body" section into this one panel.
  const [saveAsSource, setSaveAsSource] = useState(false);

  // Prefill (and re-fill) the textarea every time the panel opens. For
  // releases this seeds it with the GitHub body; the editor can trim or wipe
  // it before generating. Runs only on the closed→open edge.
  useEffect(() => {
    if (open) setBlurb(defaultBlurb ?? "");
  }, [open, defaultBlurb]);

  const featPreview = useAutofillFeaturePreview();
  const relPreview = useAutofillReleasePreview();
  const patchFeature = usePatchFeature();
  const patchRelease = usePatchRelease();
  const setItems = useSetReleaseItems();

  // Proposed values, populated after "Generate preview".
  const [featFields, setFeatFields] = useState<FeatureAutofillFields | null>(null);
  const [relResult, setRelResult] = useState<{
    narrative: string | null;
    items: AutofillReleaseItem[];
  } | null>(null);
  // Which changes the editor has kept checked.
  const [checked, setChecked] = useState<Record<string, boolean>>({});

  const previewing = featPreview.isPending || relPreview.isPending;
  const applying = patchFeature.isPending || patchRelease.isPending || setItems.isPending;
  const previewError = featPreview.error ?? relPreview.error;

  // Reset everything when the dialog closes so a reopen starts clean.
  const reset = () => {
    setBlurb("");
    setSaveAsSource(false);
    setFeatFields(null);
    setRelResult(null);
    setChecked({});
    featPreview.reset();
    relPreview.reset();
  };
  const close = () => {
    onOpenChange(false);
    reset();
  };

  // ── Feature: compute the changed-field list ──────────────────────────
  const featureChanges = useMemo(() => {
    if (kind !== "feature" || !featFields) return [];
    const cur = props.current as EnrichedFeature;
    return FEATURE_ORDER.flatMap((field) => {
      if (!(field in featFields)) return [];
      const proposed = featFields[field];
      const before = show((cur as unknown as Record<string, unknown>)[field]);
      const after = show(proposed);
      if (before === after) return [];
      return [{ field, label: FEATURE_LABELS[field], before, after }];
    });
  }, [kind, featFields, props]);

  // ── Release: narrative + items as two block-level changes ─────────────
  const releaseChanges = useMemo(() => {
    if (kind !== "release" || !relResult) return { narrativeChanged: false, itemsChanged: false, curNarr: "" };
    const rel = props.current as Release & { items?: ReleaseItem[] };
    const curNarr = rel.narrative ?? "";
    const narrativeChanged =
      relResult.narrative != null && relResult.narrative.trim() !== curNarr.trim();
    const curItems = rel.items ?? [];
    const itemsChanged =
      relResult.items.length > 0 &&
      (curItems.length !== relResult.items.length ||
        curItems.some((c, i) => c.text !== relResult.items[i]?.text));
    return { narrativeChanged, itemsChanged, curNarr };
  }, [kind, relResult, props]);

  const runPreview = async () => {
    const text = blurb.trim();
    if (!text) return;
    if (kind === "feature") {
      const r = await featPreview.mutateAsync({ id: entityId, blurb: text });
      setFeatFields(r.fields);
      // Precheck every proposed field.
      setChecked(Object.fromEntries(Object.keys(r.fields).map((k) => [k, true])));
    } else {
      const r = await relPreview.mutateAsync({ id: entityId, blurb: text });
      setRelResult({ narrative: r.narrative, items: r.items });
      setChecked({ narrative: true, items: true });
    }
  };

  const apply = async () => {
    if (kind === "feature" && featFields) {
      const patch: Record<string, unknown> = {};
      for (const c of featureChanges) {
        if (checked[c.field]) patch[c.field] = featFields[c.field];
      }
      if (Object.keys(patch).length > 0) {
        await patchFeature.mutateAsync({ id: entityId, patch: patch as Partial<EnrichedFeature> });
      }
    } else if (kind === "release" && relResult) {
      const jobs: Promise<unknown>[] = [];
      if (checked.narrative && releaseChanges.narrativeChanged) {
        jobs.push(
          patchRelease.mutateAsync({ id: entityId, patch: { narrative: relResult.narrative } }),
        );
      }
      if (checked.items && releaseChanges.itemsChanged) {
        jobs.push(setItems.mutateAsync({ id: entityId, items: relResult.items }));
      }
      if (saveAsSource) {
        jobs.push(
          patchRelease.mutateAsync({
            id: entityId,
            patch: { source_body_override: blurb.trim() || null },
          }),
        );
      }
      await Promise.all(jobs);
    }
    close();
  };

  const hasPreview = kind === "feature" ? featFields != null : relResult != null;
  const acceptedCount =
    kind === "feature"
      ? featureChanges.filter((c) => checked[c.field]).length
      : (checked.narrative && releaseChanges.narrativeChanged ? 1 : 0) +
        (checked.items && releaseChanges.itemsChanged ? 1 : 0);
  const nothingToChange =
    hasPreview &&
    (kind === "feature"
      ? featureChanges.length === 0
      : !releaseChanges.narrativeChanged && !releaseChanges.itemsChanged);

  return (
    <Dialog open={open} onOpenChange={(v) => (v ? onOpenChange(true) : close())}>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-hidden">
        {(previewing || applying) && (
          <div className="absolute inset-0 z-10 flex items-center justify-center rounded-lg bg-card/70 backdrop-blur-sm">
            <Loading label={applying ? "Applying changes" : "Reading your notes"} />
          </div>
        )}
        <DialogHeader>
          <DialogTitle>Generate with AI</DialogTitle>
          <DialogDescription>
            Paste a blurb — a Slack message, PRD paragraph, or rough note — and
            review the proposed changes before applying. Nothing is saved until
            you click Apply.
          </DialogDescription>
        </DialogHeader>

        {!hasPreview ? (
          <>
            <textarea
              value={blurb}
              onChange={(e) => setBlurb(e.target.value)}
              rows={10}
              autoFocus
              placeholder="Paste your notes here…"
              className="w-full resize-y rounded-md border border-border bg-bg px-3 py-2 text-sm text-text focus:border-text/40 focus:outline-none"
            />
            {kind === "release" && (
              <label className="flex cursor-pointer items-center gap-2 text-xs text-text-secondary">
                <input
                  type="checkbox"
                  className="h-3.5 w-3.5 accent-accent"
                  checked={saveAsSource}
                  onChange={(e) => setSaveAsSource(e.target.checked)}
                />
                Save these notes as this release's source (future generates
                start from them instead of the raw GitHub body)
              </label>
            )}
            {previewError && (
              <p className="text-xs text-destructive">
                {previewError instanceof Error ? previewError.message : "Preview failed"}
              </p>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={close}>
                Cancel
              </Button>
              <Button
                onClick={runPreview}
                loading={previewing}
                disabled={!blurb.trim()}
              >
                {previewing ? "Reading…" : "Generate preview"}
              </Button>
            </DialogFooter>
          </>
        ) : (
          <>
            <div className="max-h-[60vh] space-y-2 overflow-y-auto pr-1">
              {nothingToChange && (
                <p className="rounded-md border border-dashed border-border bg-card/40 px-4 py-8 text-center text-sm text-text-tertiary">
                  Nothing new — the notes didn't produce any changes to this{" "}
                  {kind === "feature" ? "feature" : "release"}.
                </p>
              )}

              {kind === "feature" &&
                featureChanges.map((c) => (
                  <ChangeRow
                    key={c.field}
                    label={c.label}
                    before={c.before}
                    after={c.after}
                    checked={!!checked[c.field]}
                    onToggle={() =>
                      setChecked((p) => ({ ...p, [c.field]: !p[c.field] }))
                    }
                  />
                ))}

              {kind === "release" && relResult && (
                <>
                  {releaseChanges.narrativeChanged && (
                    <ChangeRow
                      label="Narrative"
                      before={releaseChanges.curNarr}
                      after={relResult.narrative ?? ""}
                      checked={!!checked.narrative}
                      onToggle={() => setChecked((p) => ({ ...p, narrative: !p.narrative }))}
                    />
                  )}
                  {releaseChanges.itemsChanged && (
                    <ChangeRow
                      label={`Items (${(props.current as Release & { items?: ReleaseItem[] }).items?.length ?? 0} → ${relResult.items.length})`}
                      before={((props.current as Release & { items?: ReleaseItem[] }).items ?? [])
                        .map((i) => `• ${i.text}`)
                        .join("\n")}
                      after={relResult.items.map((i) => `• ${i.text}`).join("\n")}
                      checked={!!checked.items}
                      onToggle={() => setChecked((p) => ({ ...p, items: !p.items }))}
                      note="Items are replaced as a block."
                    />
                  )}
                </>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={close}>
                Cancel
              </Button>
              <Button
                onClick={apply}
                loading={applying}
                disabled={acceptedCount === 0 && !saveAsSource}
              >
                {applying
                  ? "Applying…"
                  : acceptedCount === 0 && saveAsSource
                    ? "Save source"
                    : `Apply ${acceptedCount} change${acceptedCount === 1 ? "" : "s"}`}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function ChangeRow({
  label,
  before,
  after,
  checked,
  onToggle,
  note,
}: {
  label: string;
  before: string;
  after: string;
  checked: boolean;
  onToggle: () => void;
  note?: string;
}) {
  return (
    <label className="flex cursor-pointer gap-3 rounded-md border border-border bg-card/40 p-3">
      <input
        type="checkbox"
        className="mt-0.5 h-3.5 w-3.5 shrink-0 accent-accent"
        checked={checked}
        onChange={onToggle}
      />
      <div className="min-w-0 flex-1">
        <div className="mb-1 flex items-baseline gap-2">
          <span className="text-[10.5px] font-semibold uppercase tracking-wider text-text-tertiary">
            {label}
          </span>
          {note && <span className="text-[10.5px] text-text-tertiary">· {note}</span>}
        </div>
        <div className="grid gap-1 md:grid-cols-2">
          <div className="rounded border border-border bg-bg/60 px-2 py-1.5 text-[12px] text-text-secondary">
            <div className="mb-0.5 text-[10px] font-semibold uppercase tracking-wider text-text-tertiary">
              Current
            </div>
            {before.trim() ? (
              <pre className="whitespace-pre-wrap break-words font-sans">{before}</pre>
            ) : (
              <span className="italic text-text-tertiary">(empty)</span>
            )}
          </div>
          <div className="rounded border border-border-hover bg-card px-2 py-1.5 text-[12px] text-text">
            <div className="mb-0.5 text-[10px] font-semibold uppercase tracking-wider text-text-tertiary">
              Proposed
            </div>
            <pre className="whitespace-pre-wrap break-words font-sans">{after}</pre>
          </div>
        </div>
      </div>
    </label>
  );
}
